import { Fragment, useRef, useEffect, useState } from 'react';
import { Button } from '../components/Button.jsx';
import { formatMoney, formatPercent } from '../components/format.js';
import { classifyLuck } from '../engine/planRating.js';
import { fetchScores, formatCop31Outcome, formatCountryFlag } from '../api/scores.js';
// Advanced lookup is a superset of the Classic plants (same names) plus
// the storage plants, so builds render with real names on both boards.
import { ADVANCED_PLANT_BY_ID as PLANT_BY_ID } from '../data/advancedCapacity.js';
import { GAME_MODE, DEFAULT_GAME_MODE, GAME_MODE_LABEL } from '../data/modes.js';
import { PAGE } from '../state/pages.js';

export const VIEW = {
  BEST_PLANS: 'bestPlans',
  LOWEST_COST: 'lowestCost'
};

// Best Build Plans leads because it is the board that carries the
// game's lesson: a plan is judged on what it costs on average, not on
// the one disaster draw it happened to get.
export const DEFAULT_VIEW = VIEW.BEST_PLANS;

const BOARD_CONFIG = {
  daily: {
    pageTitle: 'Daily High Scores',
    views: {
      [VIEW.BEST_PLANS]: {
        tabLabel: 'Best Build',
        title: 'Best Build Plans (today)',
        badge: 'Plan quality',
        variant: 'quality',
        description:
          'Ranked by expected cost — the average cost of each plan across many alternate disaster rolls (random outcomes). This reflects the quality of the plan, not the luck of the draw.',
        scoresKey: 'bestPlanDailyScores',
        costKey: 'expectedCost',
        costLabel: 'Expected',
        extraColumn: 'cost',
        emptyText: 'No plans have been rated today.'
      },
      [VIEW.LOWEST_COST]: {
        tabLabel: 'Lowest Cost',
        title: 'Lowest Cost Plans (today)',
        badge: 'Realised run',
        variant: 'luck',
        description:
          'Ranked by the cost actually realised on the day. The luck column shows where that run landed against alternate disaster rolls (random events) of the same plan.',
        scoresKey: 'dailyScores',
        costKey: 'totalCost',
        costLabel: 'Cost',
        showExpectedCost: true,
        extraColumn: 'luck',
        emptyText: 'No scores have been submitted today.'
      }
    }
  },
  allTime: {
    pageTitle: 'All-Time High Scores',
    views: {
      [VIEW.BEST_PLANS]: {
        tabLabel: 'Best Build',
        title: 'Best Build Plans (all-time)',
        badge: 'Plan quality',
        variant: 'quality',
        description:
          'Ranked by expected cost — the average cost of each plan across many alternate disaster rolls (random outcomes). This reflects the quality of the plan, not the luck of the draw.',
        scoresKey: 'bestPlanAllTimeScores',
        costKey: 'expectedCost',
        costLabel: 'Expected',
        extraColumn: 'cost',
        emptyText: 'No plans have been rated yet.'
      },
      [VIEW.LOWEST_COST]: {
        tabLabel: 'Lowest Cost',
        title: 'Lowest Cost Plans (all-time)',
        badge: 'Realised run',
        variant: 'luck',
        description:
          'Ranked by the cost actually realised in that run. The luck column shows where that run landed against alternate disaster rolls (random events) of the same plan.',
        scoresKey: 'allTimeScores',
        costKey: 'totalCost',
        costLabel: 'Cost',
        showExpectedCost: true,
        extraColumn: 'luck',
        emptyText: 'No scores have been submitted yet.'
      }
    }
  }
};

function formatCompletedAt(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unrecorded';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(date);
}

function formatList(items = [], fallback = 'None recorded') {
  return items.length > 0 ? items.join(', ') : fallback;
}

function formatPortfolioIds(portfolio = []) {
  const quantitiesByName = new Map();

  portfolio.forEach((id) => {
    const name = PLANT_BY_ID[id]?.name ?? id;
    quantitiesByName.set(name, (quantitiesByName.get(name) ?? 0) + 1);
  });

  const names = [...quantitiesByName.entries()]
    .sort(([firstName], [secondName]) =>
      firstName.localeCompare(secondName, 'en-GB')
    )
    .map(([name, quantity]) => (quantity > 1 ? `${name} x${quantity}` : name));

  return formatList(names);
}

function formatDisastersForYear(disasters, year) {
  const entry = disasters.find((item) => Number(item?.year) === year);
  return formatList(entry?.names ?? []);
}

// Daily scores retain their per-year build timeline. Every board also shows
// the grouped final grid stored in the flattened portfolio.
function renderBuildRows(score) {
  const byYear = Array.isArray(score.portfolioByYear) ? score.portfolioByYear : [];
  const buildRows = byYear.some((entry) => (entry?.plants ?? []).length > 0)
    ? byYear.map((entry) => (
        <div key={`build-year-${entry.year}`}>
          <dt>{`Year ${entry.year} build`}</dt>
          <dd>{formatPortfolioIds(entry.plants)}</dd>
        </div>
      ))
    : [];

  return (
    <>
      {buildRows}
      <div>
        <dt>Final grid</dt>
        <dd>{formatPortfolioIds(score.portfolio)}</dd>
      </div>
    </>
  );
}

function renderPortfolioDetails(score) {
  const disasters = Array.isArray(score.disasters) ? score.disasters : [];
  return (
    <div className="high-score-detail">
      <dl className="high-score-detail-grid">
        {renderBuildRows(score)}
        <div>
          <dt>Year 1 disasters</dt>
          <dd>{formatDisastersForYear(disasters, 1)}</dd>
        </div>
        <div>
          <dt>Year 2 disasters</dt>
          <dd>{formatDisastersForYear(disasters, 2)}</dd>
        </div>
        <div>
          <dt>Year 3 disasters</dt>
          <dd>{formatDisastersForYear(disasters, 3)}</dd>
        </div>
        <div>
          <dt>COP 31 Antalya</dt>
          <dd>{formatCop31Outcome(score.cop31Outcome)}</dd>
        </div>
        <div>
          <dt>Emissions</dt>
          <dd>{Number.isFinite(Number(score.emissions)) ? Number(score.emissions) : 'Unknown'}</dd>
        </div>
        <div>
          <dt>Shortfall cost</dt>
          <dd>{formatMoney(Number(score.shortfallCost ?? 0))}</dd>
        </div>
        {Number(score.resourceDevelopmentPremium ?? 0) > 0 ? (
          <div>
            <dt>Resource premium</dt>
            <dd>{formatMoney(Number(score.resourceDevelopmentPremium))}</dd>
          </div>
        ) : null}
        <div>
          <dt>Completed</dt>
          <dd>{formatCompletedAt(score.completedAt)}</dd>
        </div>
      </dl>
    </div>
  );
}

function PlannerCell({ score }) {
  const flag = formatCountryFlag(score.playerCountryCode);
  return (
    <span className="high-score-planner">
      {flag ? (
        <span className="high-score-flag" aria-hidden="true">
          {flag}
        </span>
      ) : null}
      <span className="high-score-planner-name">{score.playerName}</span>
    </span>
  );
}

// Entries submitted before Plan Rating shipped carry no luckPercentile,
// so they read as unrated rather than being shown as maximally unlucky.
function LuckCell({ score }) {
  const luckPercentile = Number(score.luckPercentile);
  if (!Number.isFinite(luckPercentile)) {
    return <span className="high-score-luck-unrated">Not rated</span>;
  }
  const band = classifyLuck(luckPercentile);
  return (
    <span className={`high-score-luck high-score-luck-${band.id}`}>
      <span className="high-score-luck-label">{band.shortLabel}</span>
      <span className="high-score-luck-detail">
        luckier than {formatPercent(luckPercentile)} of histories
      </span>
    </span>
  );
}

function ExpectedCostCell({ score }) {
  const expectedCost = Number(score?.expectedCost);
  if (!Number.isFinite(expectedCost)) {
    return <span className="high-score-expected-cost-unrated">Not rated</span>;
  }
  return formatMoney(expectedCost);
}

const EXTRA_COLUMN = {
  luck: {
    label: 'Luck',
    render: (score) => <LuckCell score={score} />
  },
  cost: {
    label: 'Cost',
    render: (score) => {
      const totalCost = Number(score.totalCost);
      return Number.isFinite(totalCost) ? formatMoney(totalCost) : 'Unrecorded';
    }
  }
};

function LeaderboardTable({
  title,
  badge,
  variant,
  description,
  scores,
  emptyText,
  costKey,
  costLabel,
  showExpectedCost = false,
  extraColumn
}) {
  const extra = EXTRA_COLUMN[extraColumn] ?? null;
  const columnCount = 5 + Number(showExpectedCost) + Number(Boolean(extra));
  const headingId = `${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-heading`;
  const variantClass = variant ? ` high-score-section-${variant}` : '';
  return (
    <section className={`high-score-section${variantClass}`} aria-labelledby={headingId}>
      <div className="high-score-section-heading">
        <h2 id={headingId} className="high-score-section-title">
          {title}
        </h2>
        {badge ? <span className="high-score-badge">{badge}</span> : null}
      </div>
      {description ? <p className="high-score-section-description">{description}</p> : null}
      {scores.length === 0 ? (
        <p className="high-score-status">{emptyText}</p>
      ) : (
        <div className="high-score-table-wrap">
          <table
            className={`high-score-table high-score-table-columns-${columnCount}`}
            aria-label={`${title} leaderboard`}
          >
            <thead>
              <tr>
                <th scope="col">Rank</th>
                <th scope="col">Planner</th>
                <th scope="col">Role</th>
                <th scope="col">Nationality</th>
                <th scope="col" className="high-score-rank-metric-head">
                  {costLabel}
                </th>
                {showExpectedCost ? <th scope="col">Expected</th> : null}
                {extra ? <th scope="col">{extra.label}</th> : null}
              </tr>
            </thead>
            <tbody>
              {scores.map((score, index) => {
                const scoreKey = `${title}-${score.playerName}-${score.completedAt ?? index}`;
                return (
                  <Fragment key={scoreKey}>
                    <tr className="high-score-score-row">
                      <td>{index + 1}</td>
                      <td><PlannerCell score={score} /></td>
                      <td>{score.playerRole ?? 'Unspecified role'}</td>
                      <td>{score.playerCountry ?? 'Unspecified country'}</td>
                      <td className="high-score-rank-metric">
                        {formatMoney(Number(score[costKey]))}
                      </td>
                      {showExpectedCost ? (
                        <td className="high-score-expected-cost">
                          <ExpectedCostCell score={score} />
                        </td>
                      ) : null}
                      {extra ? <td>{extra.render(score)}</td> : null}
                    </tr>
                    <tr className="high-score-detail-row">
                      <td colSpan={columnCount}>
                        <details className="high-score-disclosure">
                          <summary>Portfolio and run details</summary>
                          {renderPortfolioDetails(score)}
                        </details>
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function BoardToggle({ views, activeView, onSelect }) {
  return (
    <div className="high-score-toggle" role="tablist" aria-label="Leaderboard type">
      {Object.entries(views).map(([viewId, view]) => {
        const selected = viewId === activeView;
        return (
          <button
            key={viewId}
            type="button"
            role="tab"
            aria-selected={selected}
            className={`high-score-toggle-btn${selected ? ' high-score-toggle-btn-active' : ''}`}
            data-testid={`scores-toggle-${viewId}`}
            onClick={() => onSelect(viewId)}
          >
            {view.tabLabel}
          </button>
        );
      })}
    </div>
  );
}

export function HighScoresPage({
  board = 'daily',
  gameMode = DEFAULT_GAME_MODE,
  onNavigate,
  returnPage = PAGE.HOME
}) {
  const config = BOARD_CONFIG[board] ?? BOARD_CONFIG.daily;
  const [activeView, setActiveView] = useState(DEFAULT_VIEW);
  const modeLabel = GAME_MODE_LABEL[gameMode] ?? GAME_MODE_LABEL[DEFAULT_GAME_MODE];
  const advanced = gameMode === GAME_MODE.ADVANCED_CAPACITY;
  const mainRef = useRef(null);
  const [leaderboards, setLeaderboards] = useState({
    dailyScores: [],
    allTimeScores: [],
    bestPlanDailyScores: [],
    bestPlanAllTimeScores: []
  });
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    mainRef.current?.focus();
  }, []);

  async function loadScores() {
    setStatus('loading');
    setMessage('');
    try {
      const nextLeaderboards = await fetchScores({ mode: gameMode });
      setLeaderboards(nextLeaderboards);
      setStatus('success');
    } catch (error) {
      setStatus('error');
      setMessage(
        error instanceof Error
          ? error.message
          : 'High scores are unavailable right now.'
      );
    }
  }

  useEffect(() => {
    let active = true;

    async function loadInitialScores() {
      setStatus('loading');
      setMessage('');
      try {
        const nextLeaderboards = await fetchScores({ mode: gameMode });
        if (!active) return;
        setLeaderboards(nextLeaderboards);
        setStatus('success');
      } catch (error) {
        if (!active) return;
        setStatus('error');
        setMessage(
          error instanceof Error
            ? error.message
            : 'High scores are unavailable right now.'
        );
      }
    }

    loadInitialScores();
    return () => {
      active = false;
    };
  }, [gameMode]);

  return (
    <main
      ref={mainRef}
      tabIndex={-1}
      className="onboarding-screen onboarding-scores"
    >
      <div className="onboarding-content">
        <h1 className="onboarding-title">{config.pageTitle}</h1>
        <p className="high-score-mode" data-testid="scores-mode">
          <span
            className={
              'high-score-mode-badge' +
              (advanced ? ' high-score-mode-badge-advanced' : '')
            }
          >
            {modeLabel}
          </span>
          <span className="high-score-mode-note">
            {advanced
              ? 'Advanced runs are ranked against each other on their own board, separate from Classic.'
              : 'Classic runs only. Switch mode on the home screen for the Advanced board.'}
          </span>
        </p>

        {status === 'loading' ? (
          <p className="high-score-status" role="status">
            Loading high scores...
          </p>
        ) : null}

        {status === 'error' ? (
          <p className="high-score-status high-score-status-error" role="alert">
            {message}
          </p>
        ) : null}

        {status === 'success' ? (
          <>
            <BoardToggle
              views={config.views}
              activeView={activeView}
              onSelect={setActiveView}
            />
            {(() => {
              const view = config.views[activeView] ?? config.views[DEFAULT_VIEW];
              return (
                <LeaderboardTable
                  title={view.title}
                  badge={view.badge}
                  variant={view.variant}
                  description={view.description}
                  scores={leaderboards[view.scoresKey] ?? []}
                  emptyText={view.emptyText}
                  costKey={view.costKey}
                  costLabel={view.costLabel}
                  showExpectedCost={view.showExpectedCost}
                  extraColumn={view.extraColumn}
                />
              );
            })()}
          </>
        ) : null}

        <div className="onboarding-actions">
          <Button
            variant="secondary"
            onClick={loadScores}
            disabled={status === 'loading'}
            testId="scores-refresh"
          >
            Refresh
          </Button>
          <Button
            onClick={() => onNavigate(returnPage)}
            changesScreen
            testId="scores-back"
          >
            Back
          </Button>
        </div>
      </div>
    </main>
  );
}
